import React, { PureComponent } from 'react';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { showFlag } from 'src/redux/flags';
import { resetPendingCommit } from 'src/redux/pending-commit';
import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';
import {
  CommitAction,
  CommitResults,
  Ref,
} from 'src/sections/repository/types';
import { getCurrentRepositoryMainBranchName } from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import urls from 'src/urls/source';

import CommitModalDialog from '../components/commit-modal-dialog';
import messages from '../components/commit-modal-dialog.i18n';

const commitActionMessages = {
  commit: {
    description: messages.successFlagChangeFileDescription,
    title: messages.successFlagChangeFileTitle,
  },
  delete: {
    description: messages.successFlagDeleteFileDescription,
    title: messages.successFlagDeleteFileTitle,
  },
  rename: {
    description: messages.successFlagRenameFileDescription,
    title: messages.successFlagRenameFileTitle,
  },
};

const mapStateToProps = (state: BucketState) => {
  const { atRef, ref } = state.repository.source.section;
  const currentRef = atRef || ref;
  const mainbranch = getCurrentRepositoryMainBranchName(state);

  return {
    commitAction: state.repository.pendingCommit.action,
    branchName: currentRef ? currentRef.name : mainbranch,
    currentRef,
    fileContents: state.repository.pendingCommit.fileContents,
    filePath: state.repository.pendingCommit.filePath,
    newFilePath: state.repository.pendingCommit.newFilePath,
    parentCommitHash: state.repository.source.section.hash,
    // TODO: Force users who don't have write access to create a PR
    // https://softwareteams.atlassian.net/browse/BBCDEV-9614
    requirePullRequest: false,
  };
};

const mapDispatchToProps = {
  onClose: resetPendingCommit,
  onSuccess: (commitResults: CommitResults) =>
    showFlag({
      actions: [
        {
          content: { msg: messages.successFlagCommitLink },
          href: commitResults.commitUrl,
        },
      ],
      description: {
        msg: commitActionMessages[commitResults.commitAction].description,
      },
      id: 'commit-success',
      title: { msg: commitActionMessages[commitResults.commitAction].title },
      type: 'success',
    }),
};

type Props = RouteComponentProps & {
  commitAction?: CommitAction;
  branchName?: string;
  currentRef?: Ref;
  fileContents?: string;
  filePath?: string;
  newFilePath?: string;
  onClose: () => void;
  onSuccess: (commitResults: CommitResults) => void;
  parentCommitHash?: string;
  repositoryFullSlug: string;
  requirePullRequest: boolean;
};

class GuardedCommitModalDialog extends PureComponent<Props> {
  handleSuccess = (commitResults: CommitResults) => {
    const { commitAction, commitHash, filePath, pullRequestCreateUrl } =
      commitResults;

    if (pullRequestCreateUrl) {
      window.location.href = pullRequestCreateUrl;
      return;
    }

    const parentPath = filePath.split('/').slice(0, -1).join('/');

    const path = commitAction === 'delete' ? parentPath : filePath;

    this.props.history.push(
      urls.ui.source(this.props.repositoryFullSlug, {
        refOrHash: commitHash,
        path,
        at: this.props.currentRef ? this.props.currentRef.name : undefined,
      })
    );

    this.props.onSuccess(commitResults);
    this.props.onClose();
  };

  render() {
    // `contents` is an empty string for empty files, which can be renamed and updated
    // `contents` is `undefined` if the file hasn't been loaded yet (it's either pending,
    // or it's a binary or LFS file that we don't fetch the contents of at all)
    if (
      !this.props.commitAction ||
      (this.props.fileContents === undefined &&
        this.props.commitAction !== 'delete') ||
      !this.props.filePath
    ) {
      return null;
    }

    return (
      <CommitModalDialog
        action={this.props.commitAction}
        branchName={this.props.branchName}
        currentFilePath={this.props.filePath}
        defaultNewFilePath={this.props.newFilePath}
        fileContents={this.props.fileContents}
        onClose={this.props.onClose}
        onSuccess={this.handleSuccess}
        parentCommitHash={this.props.parentCommitHash}
        repositoryFullSlug={this.props.repositoryFullSlug}
        requirePullRequest={this.props.requirePullRequest}
      />
    );
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(GuardedCommitModalDialog);
